import React, { useState, useEffect, useCallback } from 'react';
import { Button, Skeleton, FormModal } from '../../components/ui';
import { adminAPI } from '../../services/adminApi';
import SubNavigation from '../../components/admin/SubNavigation';
import {
  Search,
  PersonOff,
  Warning,
  TrendingUp,
  Person,
  Email,
  Phone,
  CalendarToday,
  AccountCircle,
  CheckCircle,
  Cancel,
  People
} from '../../mui-icons';
import './UsersTempPage.css';

const UsersTempPage = () => {
  // Stats State
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  
  // List State
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [stageFilter, setStageFilter] = useState('');
  const [loginTypeFilter, setLoginTypeFilter] = useState('');
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, total_pages: 0 });
  
  // Detail Modal
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetail, setUserDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // Load stats
  const loadStats = async () => {
    try {
      const response = await adminAPI.getUsersTempStats();
      if (response.status === 'success') {
        setStats(response.data);
      }
    } catch (err) {
      console.error('Erro ao carregar estatísticas:', err);
    } finally {
      setStatsLoading(false);
    }
  };

  useEffect(() => {
    loadStats();
  }, []);

  // Load users
  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...(search && { search }),
        ...(stageFilter && { stage: stageFilter }),
        ...(loginTypeFilter && { login_type: loginTypeFilter })
      };

      const response = await adminAPI.getUsersTemp(params);
      
      if (response.status === 'success') {
        setUsers(response.data.items || []);
        setPagination(prev => ({
          ...prev,
          total: response.data.pagination?.total || 0,
          total_pages: response.data.pagination?.total_pages || 0
        }));
      }
    } catch (err) {
      console.error('Erro ao carregar cadastros:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, search, stageFilter, loginTypeFilter]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  // Load user detail
  const loadUserDetail = async (userId) => {
    setDetailLoading(true);
    try {
      const response = await adminAPI.getUserTemp(userId);
      if (response.status === 'success') {
        setUserDetail(response.data);
      }
    } catch (err) {
      console.error('Erro ao carregar detalhes:', err);
    } finally {
      setDetailLoading(false);
    }
  };

  // Open detail modal
  const openDetailModal = (user) => {
    setSelectedUser(user);
    setShowDetailModal(true);
    loadUserDetail(user.iduserstemp);
  };

  // Close detail modal
  const closeDetailModal = () => {
    setShowDetailModal(false);
    setSelectedUser(null);
    setUserDetail(null);
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get stage badge
  const getStageBadge = (stage, stageName) => {
    const stageMap = {
      1: { class: 'stage-1', icon: '1️⃣' },
      4: { class: 'stage-4', icon: '4️⃣' },
      5: { class: 'stage-5', icon: '⚠️' }
    };
    const { class: className, icon } = stageMap[stage] || { class: '', icon: stage };
    return (
      <span className={`stage-badge ${className}`}>
        {icon} {stageName || `Etapa ${stage}`}
      </span>
    );
  };

  // Get login type icon
  const getLoginTypeIcon = (loginType) => {
    const icons = {
      google: '🔵',
      apple: '🍎',
      microsoft: '🟦',
      email: '📧'
    };
    return icons[loginType] || '👤';
  };

  // Sub-navigation items para o grupo Usuários
  const subNavItems = [
    { path: '/admin/users', label: 'Usuários', icon: <People sx={{ fontSize: 18 }} /> },
    { path: '/admin/users-temp', label: 'Cadastros Incompletos', icon: <PersonOff sx={{ fontSize: 18 }} /> }
  ];

  return (
    <div className="users-temp-page">
      {/* Sub Navigation */}
      <SubNavigation items={subNavItems} />

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <PersonOff sx={{ fontSize: 32 }} />
          <div>
            <h1>Cadastros Incompletos</h1>
            <p>Usuários que iniciaram mas não finalizaram o cadastro</p>
          </div>
        </div>
      </div>

      {/* Stats Dashboard */}
      {!statsLoading && stats && (
        <div className="stats-dashboard">
          <div className="stats-card pn-card pn-card--stat main">
            <div className="stats-card pn-card pn-card--stat-content">
              <span className="stats-label">Total Incompleto</span>
              <span className="stats-value">{stats.total_incomplete || 0}</span>
              <span className="stats-conversion">
                Taxa de Conversão: <strong>{stats.conversion_rate || '0%'}</strong>
              </span>
            </div>
          </div>

          <div className="stats-card pn-card pn-card--stat">
            <div className="stats-icon stage-1">1️⃣</div>
            <div className="stats-card pn-card pn-card--stat-content">
              <span className="stats-label">Conta Criada</span>
              <span className="stats-value">{stats.by_stage?.stage_1_account_created?.count || 0}</span>
              <span className="stats-desc">{stats.by_stage?.stage_1_account_created?.description}</span>
            </div>
          </div>

          <div className="stats-card pn-card pn-card--stat">
            <div className="stats-icon stage-4">4️⃣</div>
            <div className="stats-card pn-card pn-card--stat-content">
              <span className="stats-label">Perfil Preenchido</span>
              <span className="stats-value">{stats.by_stage?.stage_4_profile_filled?.count || 0}</span>
              <span className="stats-desc">{stats.by_stage?.stage_4_profile_filled?.description}</span>
            </div>
          </div>

          <div className="stats-card pn-card pn-card--stat">
            <div className="stats-icon stage-5">⚠️</div>
            <div className="stats-card pn-card pn-card--stat-content">
              <span className="stats-label">Erro Migração</span>
              <span className="stats-value">{stats.by_stage?.stage_5_migration_error?.count || 0}</span>
              <span className="stats-desc">{stats.by_stage?.stage_5_migration_error?.description}</span>
            </div>
          </div>

          <div className="stats-card pn-card pn-card--stat login-types">
            <span className="stats-label">Por Tipo de Login</span>
            <div className="login-types-grid">
              <div className="login-type-item">
                <span>🔵 Google</span>
                <strong>{stats.by_login_type?.google || 0}</strong>
              </div>
              <div className="login-type-item">
                <span>📧 Email</span>
                <strong>{stats.by_login_type?.email || 0}</strong>
              </div>
              <div className="login-type-item">
                <span>🍎 Apple</span>
                <strong>{stats.by_login_type?.apple || 0}</strong>
              </div>
              <div className="login-type-item">
                <span>🟦 Microsoft</span>
                <strong>{stats.by_login_type?.microsoft || 0}</strong>
              </div>
            </div>
          </div>

          <div className="stats-card pn-card pn-card--stat recent">
            <span className="stats-label">Atividade Recente</span>
            <div className="recent-stats">
              <div className="recent-item">
                <span>Últimos 7 dias:</span>
                <strong>{stats.recent?.last_7_days || 0}</strong>
              </div>
              <div className="recent-item">
                <span>Últimos 30 dias:</span>
                <strong>{stats.recent?.last_30_days || 0}</strong>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="admin-card pn-card pn-card--default filters-card">
        <div className="filters-row">
          <div className="search-box">
            <Search className="search-icon" />
            <input 
              type="text"
              placeholder="Buscar por nome ou email..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
            />
          </div>
          
          <div className="filter-group">
            <label>Etapa</label>
            <select 
              className="admin-input"
              value={stageFilter}
              onChange={(e) => {
                setStageFilter(e.target.value);
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
            >
              <option value="">Todas</option>
              <option value="1">Etapa 1 - Conta Criada</option>
              <option value="4">Etapa 4 - Perfil Preenchido</option>
              <option value="5">Etapa 5 - Erro Migração</option>
            </select>
          </div>
          
          <div className="filter-group">
            <label>Tipo de Login</label>
            <select 
              className="admin-input"
              value={loginTypeFilter}
              onChange={(e) => {
                setLoginTypeFilter(e.target.value);
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
            >
              <option value="">Todos</option>
              <option value="google">Google</option>
              <option value="email">Email</option>
              <option value="apple">Apple</option>
              <option value="microsoft">Microsoft</option>
            </select>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="admin-card pn-card pn-card--default">
        {error ? (
          <div className="error-state">
            <Warning sx={{ fontSize: 48 }} />
            <p>{error}</p>
            <Button variant="primary" onClick={loadUsers}>
              Tentar novamente
            </Button>
          </div>
        ) : (
          <>
            <div className="table-responsive">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Usuário</th>
                    <th>Login</th>
                    <th>Localização</th>
                    <th>Etapa</th>
                    <th>Próximo Passo</th>
                    <th>Data</th>
                    <th style={{ width: '100px' }}>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    [...Array(5)].map((_, i) => (
                      <tr key={i}>
                        <td><Skeleton width={150} height={20} borderRadius={4} /></td>
                        <td><Skeleton width={60} height={20} borderRadius={4} /></td>
                        <td><Skeleton width={80} height={20} borderRadius={4} /></td>
                        <td><Skeleton width={100} height={20} borderRadius={4} /></td>
                        <td><Skeleton width={150} height={20} borderRadius={4} /></td>
                        <td><Skeleton width={100} height={20} borderRadius={4} /></td>
                        <td><Skeleton width={60} height={30} borderRadius={4} /></td>
                      </tr>
                    ))
                  ) : users.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="empty-state">
                        <PersonOff sx={{ fontSize: 48, opacity: 0.3 }} />
                        <p>Nenhum cadastro incompleto encontrado</p>
                      </td>
                    </tr>
                  ) : (
                    users.map(user => (
                      <tr key={user.iduserstemp}>
                        <td>
                          <div className="user-cell">
                            <strong>{user.name}</strong>
                            <span className="user-email">{user.mail}</span>
                          </div>
                        </td>
                        <td>
                          <span className="login-type">
                            {getLoginTypeIcon(user.login_type)} {user.login_type}
                          </span>
                        </td>
                        <td>
                          {user.country && user.state 
                            ? `${user.state}, ${user.country}`
                            : user.country || '-'
                          }
                        </td>
                        <td>{getStageBadge(user.stage, user.stage_name)}</td>
                        <td>
                          <span className="next-step">{user.next_step || '-'}</span>
                        </td>
                        <td>{formatDate(user.created_at)}</td>
                        <td>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => openDetailModal(user)}
                          >
                            Detalhes
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.total_pages > 1 && (
              <div className="pagination">
                <button 
                  className="pagination-btn"
                  disabled={pagination.page === 1}
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                >
                  Anterior
                </button>
                <span className="pagination-info">
                  Página {pagination.page} de {pagination.total_pages}
                </span>
                <button 
                  className="pagination-btn"
                  disabled={pagination.page === pagination.total_pages}
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                >
                  Próxima
                </button>
              </div>
            )}
          </>
        )}
      </div>

      <FormModal
        open={showDetailModal}
        onClose={closeDetailModal}
        title="Detalhes do Cadastro"
      >
        {detailLoading ? (
          <Skeleton width="100%" height={200} borderRadius={8} />
        ) : userDetail ? (
          <div className="user-detail-modal-body">
                {/* User Basic Info */}
                <div className="detail-section">
                  <h3>Informações do Usuário</h3>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <label><Person sx={{ fontSize: 16 }} /> Nome</label>
                      <span>{userDetail.user?.name || '-'}</span>
                    </div>
                    <div className="detail-item">
                      <label><Email sx={{ fontSize: 16 }} /> Email</label>
                      <span>{userDetail.user?.mail || '-'}</span>
                    </div>
                    <div className="detail-item">
                      <label><Phone sx={{ fontSize: 16 }} /> Telefone</label>
                      <span>{userDetail.user?.tel || '-'}</span>
                    </div>
                    <div className="detail-item">
                      <label><AccountCircle sx={{ fontSize: 16 }} /> Tipo Login</label>
                      <span>{getLoginTypeIcon(userDetail.user?.login_type)} {userDetail.user?.login_type}</span>
                    </div>
                    <div className="detail-item">
                      <label><CalendarToday sx={{ fontSize: 16 }} /> Criado em</label>
                      <span>{formatDate(userDetail.user?.created_at)}</span>
                    </div>
                    <div className="detail-item">
                      <label>País/Estado</label>
                      <span>{userDetail.user?.country || '-'} / {userDetail.user?.state || '-'}</span>
                    </div>
                  </div>
                </div>

                {/* Registration Status */}
                <div className="detail-section">
                  <h3>Status do Cadastro</h3>
                  <div className="registration-status">
                    <div className="status-header">
                      {getStageBadge(userDetail.registration_status?.stage, userDetail.registration_status?.stage_name)}
                      <div className="completion-bar">
                        <div 
                          className="completion-fill"
                          style={{ width: `${userDetail.registration_status?.completion_percentage || 0}%` }}
                        />
                      </div>
                      <span className="completion-text">
                        {userDetail.registration_status?.completion_percentage || 0}%
                      </span>
                    </div>
                    
                    <p className="status-description">
                      {userDetail.registration_status?.stage_description}
                    </p>
                    
                    <div className="next-step-box">
                      <strong>Próximo passo:</strong>
                      <p>{userDetail.registration_status?.next_step}</p>
                    </div>
                  </div>
                </div>

                {/* Fields Status */}
                <div className="detail-section">
                  <h3>Campos Preenchidos</h3>
                  <div className="fields-grid">
                    {userDetail.registration_status?.filled_fields?.map((field, i) => (
                      <div key={i} className="field-item filled">
                        <CheckCircle sx={{ fontSize: 16 }} />
                        {field}
                      </div>
                    ))}
                    {userDetail.registration_status?.missing_fields?.map((field, i) => (
                      <div key={i} className="field-item missing">
                        <Cancel sx={{ fontSize: 16 }} />
                        {field}
                      </div>
                    ))}
                  </div>
                </div>
          </div>
        ) : null}
      </FormModal>
    </div>
  );
};

export default UsersTempPage;

